import type { Session } from '@/types/session.types';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr-ca', label: 'Français Canadien' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const VALID_LANGUAGE_CODES = new Set<string>(
  SUPPORTED_LANGUAGES.map((language) => language.code)
);

export function isValidLanguageCode(
  code: string | null | undefined
): code is LanguageCode {
  return typeof code === 'string' && VALID_LANGUAGE_CODES.has(code);
}

export function getLanguageLabel(code: string | null | undefined): string {
  if (!isValidLanguageCode(code)) {
    return SUPPORTED_LANGUAGES[0].label;
  }

  return (
    SUPPORTED_LANGUAGES.find((language) => language.code === code)?.label ??
    SUPPORTED_LANGUAGES[0].label
  );
}

export function getClientLanguageLogMessage(
  orgName: string,
  clientName: string,
  languageCode: string | null,
  orgDefault?: string | null,
  action: 'set' | 'change' = 'change'
): string {
  const verb = action === 'set' ? 'Set' : 'Changed';
  const clientLabel = `${orgName} - ${clientName}`;

  if (languageCode) {
    return `${verb} Language to ${getLanguageLabel(
      languageCode
    )} for client '${clientLabel}'`;
  }

  return `${verb} Language to Default (${getLanguageLabel(
    orgDefault
  )}) for client '${clientLabel}'`;
}

export function buildDefaultLanguageOptions(): Array<{
  label: string;
  value: LanguageCode;
}> {
  return SUPPORTED_LANGUAGES.map((language) => ({
    label: language.label,
    value: language.code,
  }));
}

export function resolveEffectiveClientLanguage(
  clientLanguage: string | null | undefined,
  orgDefault: string | null | undefined
): LanguageCode {
  if (isValidLanguageCode(clientLanguage)) {
    return clientLanguage;
  }

  if (isValidLanguageCode(orgDefault)) {
    return orgDefault;
  }

  return DEFAULT_LANGUAGE;
}

export function resolvePreferredLanguage(
  preferredLanguage: string | null | undefined
): LanguageCode {
  if (isValidLanguageCode(preferredLanguage)) {
    return preferredLanguage;
  }

  return DEFAULT_LANGUAGE;
}

export function resolveEffectiveLanguage(
  userPreferred: string | null | undefined,
  clientDefault: string | null | undefined,
  orgDefault: string | null | undefined
): LanguageCode {
  if (isValidLanguageCode(userPreferred)) {
    return userPreferred;
  }

  return resolveEffectiveClientLanguage(clientDefault, orgDefault);
}

export type SessionLanguageInput = {
  user?: { preferredLanguage?: string | null };
  client?: { default_language?: string | null };
  org?: { defaultLanguage?: string | null };
};

/** Session-build helper: preferred → client → org → en. Prefer resolveSessionUiLanguage for consumers. */
export function computeEffectiveLanguage(
  session: SessionLanguageInput
): LanguageCode {
  return resolveEffectiveLanguage(
    session.user?.preferredLanguage,
    session.client?.default_language,
    session.org?.defaultLanguage
  );
}

/** Hub chrome locale: reads session.effectiveLanguage; falls back to session user/client/org. */
export function resolveSessionUiLanguage(session: Session): LanguageCode {
  if (isValidLanguageCode(session.effectiveLanguage)) {
    return session.effectiveLanguage;
  }

  return resolveEffectiveLanguage(
    session.user?.preferredLanguage,
    session.client?.default_language,
    session.org?.defaultLanguage
  );
}

/** Keeps null/invalid DB values out of Formik state to avoid backfill-on-save. */
export function normalizePreferredLanguageFormValue(
  preferredLanguage: string | null | undefined
): LanguageCode | null {
  return isValidLanguageCode(preferredLanguage) ? preferredLanguage : null;
}

/** Keeps null/invalid DB values out of Formik state; empty string means inherit org default. */
export function normalizeClientLanguageFormValue(
  code: string | null | undefined
): LanguageCode | '' {
  return isValidLanguageCode(code) ? code : '';
}

export function normalizeClientLanguageForStorage(
  formValue: string | null | undefined
): LanguageCode | null {
  if (formValue === null || formValue === undefined || formValue === '') {
    return null;
  }

  return normalizeLanguageCode(formValue);
}

export function normalizeLanguageCode(
  code: string | null | undefined
): LanguageCode | null {
  if (code === null || code === undefined || code === '') {
    return null;
  }

  const normalized = String(code).trim().toLowerCase();
  return isValidLanguageCode(normalized) ? normalized : null;
}
